/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import FormControl from '@material-ui/core/FormControl';
import RadioGroup from '@material-ui/core/RadioGroup';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {addMonths} from 'date-fns';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {smoothScrollByElement} from '@ion/core/src/utils/BrowserUtils';
import {calendarDate} from '@ion/core/src/utils/DateUtil';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import RadioButton from '@ion/dls/src/components/radio/RadioButton';
import Slider from '@ion/dls/src/components/slider/Slider';
import {useAnimatedMount} from '@ion/dls/src/hooks/useAnimatedMount';
import {usePostMessage} from '@ion/dls/src/hooks/usePostMessage';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {editAsset, getPaymentUrl, showSubscriptionDialog} from '@/store/actions/dashboard';
import {
  paymentUrlSelector,
  selectedAssetSelector,
  subscriptionPlansSelector
} from '@/store/selectors/dashboard-selectors';
import {getImageClass} from '../../components/sidesheet/AssetSideSheetDetail';
import {getStatus} from '../../components/sidesheet/SubscriptionDetails';
import {useAssetDetails} from '../../hooks/useAssetDetails';
import Images from '../img/ImagePaths';
import BaseDialog from './BaseDialog';

export interface SubscriptionDialogProps {
  isStart?: boolean,
  name?: string,
  disabled?: boolean,
  slideValue?: number,
  purchaseView?: boolean,
  onPrevious?: MouseEventHandler,
  onNext?: MouseEventHandler,
  getButtonLabel?: () => string
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  content : {
    minWidth                       : '1000px',
    display                        : 'flex',
    justifyContent                 : 'center',
    alignItems                     : 'center',
    paddingTop                     : 20,
    flexDirection                  : 'column',
    [theme.breakpoints.down('sm')] : {
      width : '95%'
    }
  },
  details : {
    width : '91%'
  },
  imageShow : {
    backgroundColor : 'white',
    pointerEvents   : 'none',
    position        : 'relative',
    marginBottom    : '3%',
    width           : '94%',
    display         : 'flex',
    padding         : '20px',
    lineHeight      : '30px',
    borderRadius    : '4px'
  },
  name : {
    fontWeight : 700
  },
  ymm : {
    color : '#666666'
  },
  deviceDetails : {
    color      : '#666666',
    fontWeight : 300
  },
  deviceValue : {
    fontWeight : 500
  },
  plans : {
    paddingLeft   : 50,
    color         : '#4F4F4F',
    paddingBottom : 20
  },
  planList : {
    display        : 'flex',
    alignContent   : 'center',
    flexWrap       : 'wrap',
    alignItems     : 'center',
    justifyContent : 'flex-end',
    flexDirection  : 'row',
    borderRadius   : '10px',
    border         : '2px solid #808080',
    padding        : '20px',
    width          : '87%',
    marginBottom   : '10px',
    marginLeft     : 50
  },
  amount : {
    position   : 'absolute',
    left       : '90%',
    top        : '10%',
    fontWeight : 500
  },
  primaryButton : {
    width                          : '191px',
    marginRight                    : '28px',
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px'
    }
  },
  imgClass : getImageClass,
  icon     : {
    marginRight : 15
  },
  status : {
    position   : 'absolute',
    right      : 50,
    textAlign  : 'end',
    color      : '#666666',
    fontWeight : 300
  },
  statusValue : {
    fontWeight    : 500,
    textTransform : 'uppercase',
    color         : ({isActive}: { isActive: boolean, color: string }) => dlsColor(isActive ? 'caGreen' : 'caRed')
  },
  hidden : {
    display : 'none'
  },
  selectedRow : {
    background : 'white'
  },
  slide : {
    color : dlsColor('caBlue')
  },
  radioBtn : {
    '&' : {
      '& .Mui-checked' : {
        color : dlsColor('caBlue')
      }
    }
  },
  price : {
    width        : 150,
    paddingRight : 6
  },
  highlightedValue : {
    fontWeight : 500
  },
  slideClass : {
    fontSize     : '14px',
    fontWeight   : 500,
    marginBottom : '-10px',
    marginTop    : '7px'
  },
  planDesc : {
    display        : 'flex',
    alignItems     : 'center',
    flexDirection  : 'column',
    justifyContent : 'center',
    paddingBottom  : '55px'
  },
  iframeContent : {
    display        : 'flex',
    minWidth       : '1000px',
    height         : '100%',
    minHeight      : 600,
    alignItems     : 'center',
    marginLeft     : '28px',
    flexDirection  : 'column',
    justifyContent : 'center'
  }
}));

const enum PageView {
  plansList = 'plansList',
  selectedPlan = 'selectedPlan',
  purchaseView = 'purchaseView'
};

enum AsperatoMessage {
  ERROR = 'asp--error',
  COMPLETE = 'asp--complete',
  CANCEL = 'asp--cancel',
  EXIT = 'asp--exit-screen'
};

const prices = {
  basePrice : 'basePrice',
  tax       : 'tax',
  total     : 'total'
};

const Actions = ({
  purchaseView,
  onPrevious,
  onNext,
  getButtonLabel,
  slideValue,
  disabled
}: SubscriptionDialogProps) => {
  const classes = useStyles();
  return (
    <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
      <div style={{width: '12%', marginLeft: '34px'}}>
        <div className={classnames(classes.slide, classes.slideClass)}>
          {translate('common:dialogs.subscription.stepsLabel', {
            step     : slideValue,
            maxSteps : 3
          })}</div>
        <Slider step={1}
          hideThumb={true}
          value={slideValue}
          className={classes.slide}
          progress
          min={0}
          max={3} />
      </div>
      <div style={{display: 'flex'}}>
        {!purchaseView && (
          <TertiaryButton className={classes.slide} onClick={onPrevious}>
            {translate('common:dialogs.subscription.previous')}
          </TertiaryButton>
        )}
        <div className={classes.primaryButton}>
          <PrimaryButton fullWidth
            disabled={disabled}
            type='submit'
            onClick={onNext}>
            {getButtonLabel()}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

/**
 * This component provides a dialog to manage the current user's Subscription
 * information.
 *
 * ```typescript
 *
 * import {SubscriptionDialog} from '@ion-bmw-portal/src/components/dialog';
 *
 * <SubscriptionDialog />
 *
 * ```
 *
 */
const extractOrigin = (url: string): string | null => {
  if (url) {
    try {
      return new URL(url)?.origin || null;
    } catch (err) {
      error(`Extract Origin Error: URL: ${url}`);
      error(err);
    }
  }
  return null;
};

export const SubscriptionDialog = () => {
  const dispatch                        = useDispatch();
  const {isVisible, onRemove}           = useAnimatedMount();
  const selectedAsset                   = useSelector(selectedAssetSelector);
  const allPlans                        = useSelector(subscriptionPlansSelector);
  const paymentUrl                      = useSelector(paymentUrlSelector);
  const title                           = translate('common:dialogs.subscription.title');
  const [selectedPlan, setSelectedPlan] = useState({});
  const [value, setValue]               = useState('');
  const [isSuccess, setIsSuccess]       = useState(false);
  const [slideValue, setSlideValue]     = useState(1);
  const [view, setView]                 = useState({
    [PageView.plansList]    : true,
    [PageView.selectedPlan] : false,
    [PageView.purchaseView] : false
  });

  const postMessage = usePostMessage(
    extractOrigin(paymentUrl),
    Object.values(AsperatoMessage)
  );

  const {currentPlan, status, color} = useAssetDetails(selectedAsset);
  const isActive                     = status === 'active';
  const classes                      = useStyles({isActive, color});

  useEffect(() => {
    if (allPlans.length > 0) {
      setSelectedPlan(allPlans[0]);
      setValue(allPlans[0].planId);
    }
  }, [allPlans]);

  useEffect(() => {
    if (postMessage !== AsperatoMessage.EXIT) {
      if (postMessage === AsperatoMessage.ERROR ||
        postMessage === AsperatoMessage.COMPLETE ||
        postMessage === AsperatoMessage.CANCEL) {
        if (postMessage === AsperatoMessage.COMPLETE) {
          dispatch(editAsset({id: selectedAsset.id}, true));
        }
        dispatch(showSubscriptionDialog(false));
      }
    } else {
      setIsSuccess(true);
    }
  }, [postMessage, selectedAsset]);

  const handleViewChange = (view) => {
    let slide = 1;
    const {subscriptionData} = selectedAsset;
    if (view === PageView.selectedPlan) {
      slide = 2;
    } else if (view === PageView.purchaseView) {
      slide = 3;
      dispatch(getPaymentUrl(selectedAsset.vin, selectedPlan.planId, subscriptionData?.subscriptionId));
    }
    setSlideValue(slide);
    setView({
      [PageView.plansList]    : false,
      [PageView.selectedPlan] : false,
      [PageView.purchaseView] : false,
      [view]                  : true
    });
    smoothScrollByElement('content');
  };

  function onClose() {
    onRemove(function() {
      dispatch(showSubscriptionDialog(false));
      if (isSuccess) {
        dispatch(editAsset({id: selectedAsset.id}, true));
      }
    });
  };

  const handleNext = function() {
    if (!view.purchaseView) {
      handleViewChange(view.plansList ? PageView.selectedPlan : PageView.purchaseView);
    } else {
      onClose();
    }
  };

  const handlePrevious = function() {
    if (!view.plansList) {
      handleViewChange(view.purchaseView ? PageView.selectedPlan : PageView.plansList);
    } else {
      dispatch(showSubscriptionDialog(false));
    }
  };

  const getButtonLabel = function() {
    if (view.selectedPlan) {
      const {currencySymbol, totalPrice} = selectedPlan || {};
      return `${translate('common:dialogs.subscription.pay')} ${currencySymbol}${totalPrice}`;
    }
    return translate(`common:dialogs.subscription.${view.plansList ? 'next' : 'done'}`);
  };

  const PlanDetails = (plan) => (
    <div>
      <div className={classes.name}>{plan.name}</div>
      <div>{plan.description}</div>
    </div>
  );

  const SubscriptionDate = ({isStart}: SubscriptionDialogProps) => {
    const label          = translate(`common:dialogs.subscription.${isStart ? 'start' : 'expiry'}Date`);
    const startDate      = isActive ? currentPlan?.expiryDate : new Date();
    const endDate        = isActive
      ? addMonths(new Date(startDate), selectedPlan.termMonths)
      : addMonths(new Date(), selectedPlan.termMonths);
    const date           = isStart ? startDate : endDate;
    return (
      <div className={classes.deviceDetails}>
        {label} - <span className={classes.deviceValue}>{calendarDate(date, 'DD MMM YYYY')}</span>
      </div>
    );
  };

  const PlanSelected = ({name}: SubscriptionDialogProps) => {
    const label = translate(`common:dialogs.subscription.${name}`);
    let value = 0;
    if (name === prices.basePrice) {
      value = selectedPlan?.totalPrice - selectedPlan?.tax;
    } else if (name === prices.tax) {
      value = selectedPlan?.tax;
    } else if (name === prices.total) {
      value = selectedPlan?.totalPrice;
    }

    return (
      <div style={{display: 'flex'}}>
        <div className={classes.price}>{label}</div>
        <div className={classnames((name === prices.total ? classes.slide : ''), classes.highlightedValue)}>
          {selectedPlan?.currencySymbol}{value.toFixed(2)}
        </div>
      </div>
    );
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, plan) => {
    setValue((event.target as HTMLInputElement).value);
    setSelectedPlan(plan);
  };

  return (
    <BaseDialog show={isVisible}
      title={title}
      onClose={onClose}
      actions={<Actions purchaseView={view.purchaseView}
        disabled={view.plansList && allPlans?.length === 0}
        onPrevious={handlePrevious}
        slideValue={slideValue}
        onNext={handleNext}
        getButtonLabel={getButtonLabel} />}>
      {(view.plansList || view.selectedPlan) && (
        <div id={'content'} className={classes.content}>
          <div style={{paddingBottom: '30px'}}>
            {translate('common:dialogs.subscription.subTitle')}
          </div>
          {(view.plansList || view.selectedPlan) && (
            <div className={classes.details}>
              <div className={classes.imageShow}>
                <img src={Images.BMWAvatar} className={classnames(classes.imgClass, classes.icon)} />
                <div>
                  <div className={classes.name}>{selectedAsset.name}</div>
                  <div className={classes.ymm}>{selectedAsset.year} {selectedAsset.make} {selectedAsset.model}</div>
                  <div className={classes.deviceDetails}>
                    {translate('common:dialogs.subscription.vin')} - <span className={classes.deviceValue}>{selectedAsset.vin}</span>
                  </div>
                </div>
                <div className={classes.status}>
                  {translate('common:dialogs.subscription.subscription')} - <span className={classes.statusValue}>
                    {getStatus(selectedAsset?.subscriptionData?.currentPlan)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {(view.plansList || view.selectedPlan) && (
        <div className={classes.plans}>
          {view.plansList ? translate('common:dialogs.subscription.choosePlan') : translate('common:dialogs.subscription.selectedPlan')}
        </div>
      )}
      {view.plansList && (
        <div>
          {allPlans.map((plan, i) => {
            const isSelected = plan.planId === selectedPlan?.planId;
            return (
              <div key={`plan-${i}`} className={classes.planList} style={{background: isSelected ? 'white' : '#F2F2F2'}}>
                <FormControl component="fieldset">
                  <RadioGroup className={classes.radioBtn}
                    value={value}
                    onChange={function(e) { handleChange(e, plan); }}>
                    <RadioButton label={PlanDetails(plan)}
                      checked={isSelected}
                      value={plan?.planId} />
                    <span className={classes.amount} style={{color: dlsColor(isSelected ? 'caBlue': 'black')}}> {plan.currencySymbol}{plan.totalPrice}</span>
                  </RadioGroup>
                </FormControl>
              </div>
            );
          })}
        </div>
      )}
      {view.selectedPlan && (
        <div className={classes.planDesc}>
          <div className={classes.details}>
            <div className={classes.imageShow}>
              <div>
                <div className={classes.name}>{selectedPlan?.name}</div>
                <div className={classes.ymm} style={{width: '89%'}}>{selectedPlan?.description}</div>
                <SubscriptionDate isStart />
                <SubscriptionDate isStart={false} />
              </div>
              <div className={classes.status}>
                {Object.keys(prices).map((price) => (
                  <PlanSelected key={`price-${price}`} name={prices[price]} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {view.purchaseView && (
        <div className={classes.iframeContent}>
          <iframe width='100%' height='100%' src={paymentUrl} />
        </div>
      )}
    </BaseDialog>
  );
};

export default SubscriptionDialog;
